import type { LinkMode, SiteLegacy } from '@tabel/shared/schemas/sites';
import { SITE_TEXTS, STEP_BODY, localDate } from '@tabel/shared/sites/step-texts';
import { useQuery } from '@tanstack/react-query';
import { KeyRound, type LucideIcon, Power, RefreshCw, UserX } from 'lucide-react';
import { type ButtonHTMLAttributes, useEffect } from 'react';
import { Button, ConfirmDialog, ConfirmNameDialog, SettingsRow } from '../../../components/ui';
import { reportClientError } from '../../../lib/errors';
import { useRole } from '../../../lib/role';
import { siteConsequences, sitesKeys } from '../api';
import { SCREEN, shouldReport } from '../parts';
import { DANGER } from '../texts';
import { type SiteDialog, useSite } from './actions';

/*
 * «Опасная зона» страницы площадки (007 T055; spec.md US7, FR-033, FR-061, FR-062; ux.md §11: «Опасные действия — в
 * отдельном блоке «Опасная зона» с красным заголовком и красной текстовой кнопкой. Сплошная красная кнопка — только
 * в окне подтверждения»). Строки `SettingsRow`:
 * - «Выдать новый пароль» и при VPN «Выдать новый файл» — старые данные входа перестают работать сразу;
 * - «Сменить способ связи» — площадка возвращается к шагам 3 и 4;
 * - «Отключить площадку» — окно с вводом названия, тост «Отменить»; у отключённой площадки — «Подключить снова».
 * Каждое действие — через окно подтверждения; права проверяет сервер (`sites.secret.show`, `sites.step`,
 * `sites.disable`).
 */

export function DangerZone() {
  const { detail, act, busy, setDialog } = useSite();
  const s = detail.site;
  const prepared = detail.steps.find((x) => x.step === 1)?.status === 'ok';
  const disabled = s.state === 'disabled';
  const other: LinkMode = s.linkMode === 'direct' ? 'vpn' : 'direct';
  const off = busy !== null;
  return (
    <section aria-labelledby="site-danger" className="mt-8">
      <h2 id="site-danger" className="t-set-sub bad">
        {DANGER.title}
      </h2>
      {detail.legacy && !disabled && <LegacyRow legacy={detail.legacy} />}
      {prepared && !disabled && (
        <>
          <SettingsRow title={DANGER.password.title} description={DANGER.password.text}>
            <DangerButton
              icon={KeyRound}
              disabled={off}
              onClick={() => setDialog({ kind: 'reissue', what: 'password' })}
            >
              {STEP_BODY[4].reissue}
            </DangerButton>
          </SettingsRow>
          {s.linkMode === 'vpn' && (
            <SettingsRow title={DANGER.vpnKey.title} description={DANGER.vpnKey.text}>
              <DangerButton
                icon={KeyRound}
                disabled={off}
                onClick={() => setDialog({ kind: 'reissue', what: 'vpn_key' })}
              >
                {STEP_BODY[3].reissue}
              </DangerButton>
            </SettingsRow>
          )}
          <SettingsRow title={DANGER.link.title} description={DANGER.link.text}>
            <DangerButton
              icon={RefreshCw}
              disabled={off}
              onClick={() => setDialog({ kind: 'link', mode: other })}
            >
              {DANGER.link.to(SITE_TEXTS.linkMode[other])}
            </DangerButton>
          </SettingsRow>
        </>
      )}
      {disabled ? (
        <SettingsRow title={DANGER.enable.title} description={DANGER.enable.text}>
          <Button icon={Power} disabled={off} onClick={() => act.enable()}>
            {DANGER.enable.button}
          </Button>
        </SettingsRow>
      ) : (
        <SettingsRow title={DANGER.disable.title} description={DANGER.disable.text}>
          <DangerButton icon={Power} disabled={off} onClick={() => setDialog({ kind: 'disable' })}>
            {DANGER.disable.title}
          </DangerButton>
        </SettingsRow>
      )}
    </section>
  );
}

/**
 * «Отключить прежнюю учётную запись» — только у первой площадки (US5, research R17, `SiteDetail.legacy`). Нельзя —
 * кнопка недоступна, причина написана под пояснением (ux.md §11: «неактивное поле объясняет причину»).
 */
function LegacyRow({ legacy }: { legacy: SiteLegacy }) {
  const { detail, busy, setDialog } = useSite();
  const L = DANGER.legacy;
  const why = legacy.canRetire ? null : legacyReason(legacy, detail.site.tzOffset);
  return (
    <SettingsRow
      title={L.title}
      description={
        <>
          {L.text}
          {why && <span className="mt-1 block text-ink-2">{why}</span>}
        </>
      }
    >
      {({ descriptionId }) => (
        <DangerButton
          icon={UserX}
          disabled={!legacy.canRetire || busy !== null}
          aria-describedby={descriptionId}
          onClick={() => setDialog({ kind: 'retire' })}
        >
          {L.title}
        </DangerButton>
      )}
    </SettingsRow>
  );
}

/** Почему нельзя отключить прежнюю учётную запись — по коду `legacy.reason`; дата — по поясу площадки. */
function legacyReason(legacy: SiteLegacy, tz: string): string | null {
  const R = DANGER.legacy.reasons;
  switch (legacy.reason) {
    case 'observing':
      return legacy.until ? R.observing(localDate(legacy.until, tz)) : null;
    case null:
      return null;
    default:
      return R[legacy.reason];
  }
}

/**
 * Красная текстовая кнопка «Опасной зоны» — класс дизайн-системы `t-btn t-btn-danger-text` (на телефоне высота
 * `size-tap`, `bundle.css`). Не `Button`: его цвет текста из утилит перекрыл бы красный цвет класса.
 */
function DangerButton({
  icon: Icon,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { icon: LucideIcon }) {
  return (
    <button type="button" className="t-btn t-btn-danger-text" {...rest}>
      <Icon className="t-ico" aria-hidden />
      {children}
    </button>
  );
}

/**
 * Последствия отключения (FR-062, R20): что останется — числа с сервера (`GET …/consequences`). Текст живёт только в
 * открытом окне, поэтому числа запрашиваются при каждом открытии и в кэше не остаются. Не загрузились — окно работает
 * без чисел, с короткой фразой; сбой уходит в серверный журнал.
 */
function DisableText() {
  const { detail } = useSite();
  const { role } = useRole();
  const code = detail.site.code;
  const q = useQuery({
    queryKey: sitesKeys.consequences(role, code),
    queryFn: () => siteConsequences(code),
    gcTime: 0,
    staleTime: 0,
  });
  useEffect(() => {
    if (q.error && shouldReport(q.error)) reportClientError(SCREEN, 'consequences', q.error);
  }, [q.error]);
  const D = DANGER.disable;
  // Числа есть — «Останутся: …» вместо общих слов «Отметки, люди и табели останутся».
  const lead = q.data ? D.confirmStop : D.confirmText;
  const numbers = q.data
    ? D.consequences(q.data.passes, q.data.people, q.data.timesheets)
    : q.isError
      ? D.consequencesFailed
      : D.consequencesLoading;
  return (
    <>
      <span>{lead}</span>{' '}
      <span className="tabular-nums" aria-live="polite">
        {numbers}
      </span>
    </>
  );
}

/** Окна подтверждения «Опасной зоны» и шагов 3–4 («Выдать новый …», смена способа связи, отключение). */
export function DangerDialogs({ dialog }: { dialog: SiteDialog | null }) {
  const { detail, act, busy, setDialog } = useSite();
  const close = () => setDialog(null);
  const name = detail.site.name;
  const reissue = dialog?.kind === 'reissue' ? dialog.what : null;
  const link = dialog?.kind === 'link' ? dialog.mode : null;
  const r = reissue === 'vpn_key' ? DANGER.vpnKey : DANGER.password;
  return (
    <>
      <ConfirmDialog
        open={reissue !== null}
        onClose={close}
        onConfirm={() => reissue && void act.reissue(reissue)}
        title={r.confirmTitle(name)}
        description={r.confirmText}
        confirmLabel={reissue === 'vpn_key' ? STEP_BODY[3].reissue : STEP_BODY[4].reissue}
        icon={KeyRound}
        busy={busy !== null}
      />
      <ConfirmDialog
        open={link !== null}
        onClose={close}
        onConfirm={() => link && act.confirmLink(link)}
        title={DANGER.link.confirmTitle(link ? SITE_TEXTS.linkMode[link] : '')}
        description={DANGER.link.confirmText}
        confirmLabel={DANGER.link.title}
        icon={RefreshCw}
        busy={busy !== null}
      />
      <ConfirmNameDialog
        open={dialog?.kind === 'disable'}
        onClose={close}
        onConfirm={(typed) => void act.disable(typed)}
        title={DANGER.disable.confirmTitle(name)}
        description={<DisableText />}
        name={name}
        fieldLabel={DANGER.disable.fieldLabel}
        confirmLabel={DANGER.disable.title}
        icon={Power}
        busy={busy !== null}
      />
      <ConfirmNameDialog
        open={dialog?.kind === 'retire'}
        onClose={close}
        onConfirm={(typed) => void act.retireLegacy(typed)}
        title={DANGER.legacy.confirmTitle(name)}
        description={DANGER.legacy.confirmText}
        name={name}
        fieldLabel={DANGER.disable.fieldLabel}
        confirmLabel={DANGER.legacy.title}
        icon={UserX}
        busy={busy !== null}
      />
    </>
  );
}
